import { useCallback, useEffect, useMemo, useState } from 'react'
import { FolderTree, Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Drawer from '../../components/Drawer'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { EmptyState, LoadingState } from '../../components/States'
import { useToast } from '../../components/toastContext'
import { deleteCategory, listCategories, saveCategory } from './incidentsService'
import { incidentsStrings as S } from './strings'
import { serverText } from '../../lib/i18n'

const BLANK = { id: 0, nameAr: '', nameEn: '', parentId: '' }

/**
 * إعدادات الحوادث السيبرانية — the categories a reporter picks from.
 *
 * The button that opens this has been on the page since it was built and did nothing; the API has
 * had add, update and delete all along. They matter more than they look: **the feature cannot be
 * switched on until at least one category exists** — `update-report-enabled` refuses and stores
 * `false` — so this drawer is the first stop on a fresh install, not an afterthought.
 *
 * Categories nest one level. A category with a parent is a sub-category; there is no third tier,
 * so the parent select offers only the top-level ones.
 */
export default function CategoriesDrawer({ open, onClose, onChanged }) {
  const toast = useToast()

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(BLANK)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)

  const load = useCallback(
    () =>
      listCategories()
        .then((data) => {
          setRows(data)
          setLoading(false)
        })
        .catch((error) => {
          setRows([])
          setLoading(false)
          toast.error(error?.message || S.actionFailed)
        }),
    [toast],
  )

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setForm(BLANK)
    setEditing(false)
    load()
  }, [open, load])

  /* Top level first, each with its children under it — the same shape the reporter's category
     picker shows, so what is edited here is what they will see. */
  const tree = useMemo(() => {
    const tops = rows.filter((row) => row.parentId == null)
    return tops.map((top) => ({
      ...top,
      children: rows.filter((row) => row.parentId === top.id),
    }))
  }, [rows])

  const parentOptions = useMemo(
    () =>
      rows
        .filter((row) => row.parentId == null && row.id !== form.id)
        .map((row) => ({ value: String(row.id), label: row.name })),
    [rows, form.id],
  )

  const edit = (row) => {
    setForm({
      id: row.id,
      nameAr: row.nameAr,
      nameEn: row.nameEn,
      parentId: row.parentId == null ? '' : String(row.parentId),
    })
    setEditing(true)
  }

  const submit = async () => {
    if (!form.nameAr.trim() || !form.nameEn.trim()) return toast.error(S.namesRequired)
    setSaving(true)
    const result = await saveCategory(form)
    setSaving(false)
    if (!result.ok) return toast.error(result.message || S.actionFailed)
    toast.success(serverText(result.message) || S.saved)
    setForm(BLANK)
    setEditing(false)
    setLoading(true)
    await load()
    onChanged?.()
    return undefined
  }

  const confirmDelete = async () => {
    const target = pendingDelete
    setPendingDelete(null)
    const result = await deleteCategory(target.id)
    if (!result.ok) return toast.error(result.message || S.actionFailed)
    toast.success(S.deleted)
    setLoading(true)
    await load()
    onChanged?.()
    return undefined
  }

  const row = (item, isChild) => (
    <li key={item.id} className={`inc-cat__row${isChild ? ' inc-cat__row--child' : ''}`}>
      <span className="inc-cat__names">
        <bdi className="inc-cat__name">{item.name}</bdi>
        <bdi className="inc-cat__alt">{item.name === item.nameAr ? item.nameEn : item.nameAr}</bdi>
      </span>
      <span className="data-table__row-actions">
        <IconButton icon={Pencil} label={S.edit} onClick={() => edit(item)} />
        <IconButton icon={Trash2} label={S.delete} tone="danger" onClick={() => setPendingDelete(item)} />
      </span>
    </li>
  )

  return (
    <>
      <Drawer
      dialog
        open={open}
        size="md"
        icon={FolderTree}
        title={S.manageCategories}
        subtitle={S.categoriesHint}
        onClose={onClose}
        footer={
          <>
            <Button onClick={submit} disabled={saving}>
              {!editing && <Plus size={16} strokeWidth={1.75} />}
              {editing ? S.apply : S.addNew}
            </Button>
            {editing && (
              <Button
                variant="ghost"
                onClick={() => {
                  setForm(BLANK)
                  setEditing(false)
                }}
              >
                {S.cancel}
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              {S.close}
            </Button>
          </>
        }
      >
        <FormRow label={S.category.nameAr}>
          {(id) => (
            <TextField
              id={id}
              value={form.nameAr}
              onChange={(value) => setForm((f) => ({ ...f, nameAr: value }))}
            />
          )}
        </FormRow>
        <FormRow label={S.category.nameEn}>
          {(id) => (
            <TextField
              id={id}
              value={form.nameEn}
              onChange={(value) => setForm((f) => ({ ...f, nameEn: value }))}
            />
          )}
        </FormRow>
        <FormRow label={S.category.parent} hint={S.category.parentHint}>
          {(id) => (
            <Select
              id={id}
              value={form.parentId}
              onChange={(value) => setForm((f) => ({ ...f, parentId: value }))}
              options={parentOptions}
              placeholder={S.category.noParent}
            />
          )}
        </FormRow>

        <h4 className="inc-cat__heading">{S.category.existing}</h4>
        {loading && <LoadingState />}
        {!loading && tree.length === 0 && <EmptyState icon={FolderTree} title={S.category.empty} />}
        {!loading && tree.length > 0 && (
          <ul className="inc-cat__list">
            {tree.map((top) => (
              <li key={top.id}>
                <ul className="inc-cat__group">
                  {row(top, false)}
                  {top.children.map((child) => row(child, true))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </Drawer>

      {/* Deleting takes every sub-category with it, and the API says so only by refusing when one
          of them is in use. The confirm says it up front. */}
      <ConfirmDialog
        open={pendingDelete != null}
        message={S.category.deleteConfirm}
        confirmLabel={S.delete}
        cancelLabel={S.cancel}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
